// src/shared/components/TopNavBar.tsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiLogOut, FiBell, FiUser, FiChevronDown, FiMail, FiShield, FiSettings } from "react-icons/fi";
import { useCurrentUser } from "../../hooks/useCurrentUser";

interface TopNavBarProps {
  user?: any;
  handleLogout: () => void;
}

const TopNavBar: React.FC<TopNavBarProps> = ({ handleLogout }) => {
  const navigate = useNavigate();
  const { user, clearUser } = useCurrentUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initials = user
    ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase()
    : "?";

  return (
    // Deliberately NOT fixed: the bar is a normal flex child of AppLayout, so
    // content is pushed below it structurally. When it was fixed it occupied no
    // layout space and every page had to remember `pt-16` — many didn't, which
    // hid page content and the top of the sidebar behind it.
    <nav className="relative z-50 h-16 shrink-0 w-full bg-white shadow flex items-center justify-between px-8 py-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate("/encounters")}
          aria-label="Go to home"
          className="flex items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <img
            src="/docfi-logo.png"
            alt="DocFi"
            className="w-9 h-9 rounded-lg object-cover transition-transform hover:scale-105"
          />
        </button>
      </div>

      <div className="flex items-center gap-4">
        <button className="p-2 rounded-full hover:bg-gray-100 transition">
          <FiBell className="w-5 h-5 text-gray-500" />
        </button>

        {user && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition"
            >
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {initials}
              </div>
              <div className="flex flex-col text-left">
                <div className="flex items-center gap-1.5 leading-tight">
                  <span className="text-sm font-semibold text-gray-800">
                    Dr. {user.first_name} {user.last_name}
                  </span>
                  {user.is_staff && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-600 text-white px-1.5 py-0.5 rounded">
                      Admin
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500 capitalize leading-tight">
                  {user.profile?.user_type ?? "doctor"}
                </span>
              </div>
              <FiChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                {/* Profile header */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-lg font-bold">
                      {initials}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">
                          Dr. {user.first_name} {user.last_name}
                        </p>
                        {user.is_staff && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-600 text-white px-1.5 py-0.5 rounded">
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 capitalize">{user.profile?.user_type ?? "doctor"}</p>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                  {/* Accounts are provisioned with username === email, so showing
                      both rendered the same address twice. Only surface the
                      username when it is genuinely something different. */}
                  {user.username &&
                    user.username.toLowerCase() !==
                      (user.email ?? "").toLowerCase() && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiUser className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="truncate">{user.username}</span>
                      </div>
                    )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiMail className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </div>
                  {user.profile?.phone_number && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiShield className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>{user.profile.phone_number}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-2 py-2">
                  <button
                    onClick={() => { setDropdownOpen(false); navigate("/account"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition font-medium"
                  >
                    <FiSettings className="w-4 h-4 text-gray-400" />
                    Account settings
                  </button>
                  <button
                    onClick={() => { setDropdownOpen(false); clearUser(); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition font-medium"
                  >
                    <FiLogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default TopNavBar;
